import _ from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import TabPanel from '@/components/ui/tab/TabPanel'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import PageContainer from '@/components/ui/containers/PageContainer'
import Tabs from '@/components/ui/tabs/Tabs'

import {
  useAcceptWaitingRoomConsultationMutation,
  useGetMyWaitingRoomFairStatusQuery,
  useGetWaitingRoomActiveConsultationsQuery,
  useGetWaitingRoomQuery,
  usePassWaitingRoomOfferMutation,
} from '@/core/services/waiting-room'
import { ConsultationReadinessStatus, ConsultationStatus } from '@/core/models/consultation.model'
import { ReadinessChecklistSummary } from '@/core/models/waiting-room.model'
import { getSocket } from '@/core/api/socket'
import { SocketEvent } from '@/core/types/socket-event'

import NotFoundPage from '@/pages/error/NotFoundPage'
import useMetadataTitle from '@/hooks/useMetadataTitle'
import { useIsSA } from '@/hooks/useIsSA'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import {
  enterWaitingRoomSocketEvent,
  leaveWaitingRoomSocketEvent,
  doctorStatusUpdated,
  waitingRoomDoctorsStatusReceived,
  doctorHeartbeatSocketEvent,
  clearDoctorStatuses,
} from '@/redux/reducers/waiting-room.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import { ActionsButton } from './ActionsButton'
import { ConsultationPreviewDrawer } from './ConsultationPreviewDrawer'
import { WaitingConsultationsTable } from './WaitingConsultationsTable'
import { Info } from './Info'
import { SettingButtons } from './SettingButtons'
import { useWaitingRoomJoined } from '../model/useWaitingRoomJoined'
import { DoctorsTab } from './DoctorsTab'

export function WaitingRoomPage() {
  const appDispatch = useAppDispatch()
  const { waitingRoomId } = useParams<'waitingRoomId'>()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: waitingRoom, isLoading: isFetchingWaitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken, { refetchOnMountOrArgChange: true })
  const { data: activeConsultations = [] } = useGetWaitingRoomActiveConsultationsQuery(waitingRoomId ?? skipToken)
  const [acceptConsultation, { isLoading: isAccepting }] = useAcceptWaitingRoomConsultationMutation()
  const [passOffer, { isLoading: isPassing }] = usePassWaitingRoomOfferMutation()
  const [acceptingConsultationId, setAcceptingConsultationId] = useState<string | null>(null)

  // SEHA (en-SA) only: read-only case preview before accepting/joining. null = drawer closed.
  const isSA = useIsSA()
  const [previewConsultationId, setPreviewConsultationId] = useState<string | null>(null)
  const handlePreviewConsultation = isSA ? setPreviewConsultationId : undefined

  const allowPatientSelection = waitingRoom?.allowPatientSelection ?? false

  const { data: fairStatus } = useGetMyWaitingRoomFairStatusQuery(waitingRoomId ?? skipToken)
  const readinessConfig = waitingRoom?.readinessConfig
  const readinessEnabled = readinessConfig?.enabled ?? false
  const prioritizeReady = readinessEnabled && (readinessConfig?.prioritizeReady ?? false)

  // Local ticking clock so a pending cooldown re-enables the Accept button without a refetch.
  const [nowTick, setNowTick] = useState(Date.now())
  useEffect(() => {
    if (!fairStatus?.enabled) return
    const id = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(id)
  }, [fairStatus?.enabled])

  const cooldownSecondsRemaining = fairStatus?.cooldownUntil
    ? Math.max(0, Math.ceil((new Date(fairStatus.cooldownUntil).getTime() - nowTick) / 1000))
    : 0
  // Doctor-scoped (same for every row): why this doctor cannot accept right now, if at all.
  // In 'offer' mode the cap/cooldown are advisory — the real gate is whether it's your turn — so
  // we never show the disabled-Accept reason there (the offer chips carry that information).
  const offerStrategy = fairStatus?.strategy === 'offer'
  const acceptDisabledReason: 'cooldown' | 'capReached' | undefined =
    !fairStatus?.enabled || offerStrategy
      ? undefined
      : cooldownSecondsRemaining > 0
      ? 'cooldown'
      : fairStatus.myActiveCount >= fairStatus.maxConcurrent
      ? 'capReached'
      : undefined

  // 'offer' mode: the live reservation of the head patient and its remaining hold time.
  const offer = offerStrategy ? fairStatus?.offer ?? null : null
  const offerSecondsRemaining = offer?.offerExpiresAt
    ? Math.max(0, Math.ceil((new Date(offer.offerExpiresAt).getTime() - nowTick) / 1000))
    : 0
  // 'offer' mode: everyone online passed -> countdown until the round restarts and the head
  // patient is offered again.
  const nextRoundSecondsRemaining =
    offerStrategy && !offer && fairStatus?.nextRoundAt
      ? Math.max(0, Math.ceil((new Date(fairStatus.nextRoundAt).getTime() - nowTick) / 1000))
      : 0

  const waitingConsultations = useMemo(() => {
    const checklists = waitingRoom?.readinessChecklists
    const scheduled = _.filter(
      waitingRoom?.consultations,
      (consultation) => consultation.status === ConsultationStatus.Scheduled && !consultation.medecin
    )

    // Group consultations by groupId
    const grouped: Record<string, typeof scheduled> = {}
    const ungrouped: typeof scheduled = []

    scheduled.forEach((consultation) => {
      if (consultation.groupId) {
        if (!grouped[consultation.groupId]) grouped[consultation.groupId] = []
        grouped[consultation.groupId].push(consultation)
      } else {
        ungrouped.push(consultation)
      }
    })

    type Entry = {
      id: string
      patient: string
      pds: string
      startDate: string
      paid?: boolean
      price?: number
      paymentType?: any
      isGroup?: boolean
      groupId?: string
      groupPatients?: string[]
      groupConsultationIds?: string[]
      readiness: 'ready' | 'preparing'
      checklist?: ReadinessChecklistSummary
    }
    const entries: Entry[] = []

    // Grouped consultations as single rows (a group is ready only if every sibling is ready)
    Object.entries(grouped).forEach(([groupId, consultations]) => {
      const first = consultations[0]
      const patientNames = consultations.map((c) => _.trim(`${c.patient?.userInfo?.name ?? ''} ${c.patient?.userInfo?.surname ?? ''}`))
      const ready = consultations.every((c) => c.readiness?.status === ConsultationReadinessStatus.Ready)
      entries.push({
        id: first._id,
        patient: patientNames.join(', '),
        pds: first.pds ? `${first.pds.userInfo?.name} ${first.pds.userInfo?.surname}` : '',
        startDate: first.startDate,
        paid: first.paid,
        price: first.price,
        paymentType: first.paymentType,
        isGroup: true,
        groupId,
        groupPatients: patientNames,
        groupConsultationIds: consultations.map((c) => c._id),
        readiness: ready ? 'ready' : 'preparing',
        // The checklist submission attaches to whichever sibling the operator prepared
        checklist: consultations.map((c) => checklists?.[c._id]).find(Boolean),
      })
    })

    // Ungrouped consultations
    ungrouped.forEach((consultation) => {
      entries.push({
        id: consultation._id,
        patient: _.trim(`${consultation.patient?.userInfo?.name ?? ''} ${consultation.patient?.userInfo?.surname ?? ''}`),
        pds: consultation.pds ? `${consultation.pds.userInfo?.name} ${consultation.pds.userInfo?.surname}` : '',
        startDate: consultation.startDate,
        paid: consultation.paid,
        price: consultation.price,
        paymentType: consultation.paymentType,
        readiness: consultation.readiness?.status === ConsultationReadinessStatus.Ready ? 'ready' : 'preparing',
        checklist: checklists?.[consultation._id],
      })
    })

    // When prioritizeReady is on, float ready patients to the top (stable within each partition).
    // This only reorders the doctor's presentation; the queue source order is untouched.
    const ordered = prioritizeReady
      ? [...entries.filter((e) => e.readiness === 'ready'), ...entries.filter((e) => e.readiness !== 'ready')]
      : entries

    return ordered.map((entry, index) => ({
      ...entry,
      // 'offer' mode: only the doctor currently holding this patient's offer may accept it.
      // 'cap'/legacy mode: the FIFO head (or any row when free patient selection is on).
      isConsultable: offerStrategy ? offer?.consultationId === entry.id && !!offer?.isMyTurn : allowPatientSelection || index === 0,
    }))
  }, [allowPatientSelection, waitingRoom, prioritizeReady, offerStrategy, offer])

  useMetadataTitle(waitingRoom ? `${waitingRoom.name}${waitingConsultations.length}` : undefined)

  const isCreator = authUser?._id === waitingRoom?.createdBy
  const waitingRoomJoined = useWaitingRoomJoined(waitingRoom)

  // Socket connection and event listeners
  useEffect(() => {
    if (!waitingRoomId) return

    const connectSocket = async () => {
      const socket = await getSocket()

      // Enter waiting room
      appDispatch(enterWaitingRoomSocketEvent(waitingRoomId))

      // Listen for doctor status updates
      const handleDoctorStatusUpdate = (data: { doctorId: string; status: 'available' | 'in_consultation' | 'offline'; consultationId?: string }) => {
        appDispatch(doctorStatusUpdated(data))
      }

      // Listen for bulk doctor status updates
      const handleWaitingRoomDoctorsStatus = (
        data: {
          doctorId: string
          status: 'available' | 'in_consultation' | 'offline'
          consultationId?: string
        }[]
      ) => {
        appDispatch(waitingRoomDoctorsStatusReceived(data))
      }

      // Add listeners
      socket.on(SocketEvent.DOCTOR_STATUS_UPDATED, handleDoctorStatusUpdate)
      socket.on(SocketEvent.WAITING_ROOM_DOCTORS_STATUS, handleWaitingRoomDoctorsStatus)

      return () => {
        // Remove listeners and leave waiting room
        socket.off(SocketEvent.DOCTOR_STATUS_UPDATED, handleDoctorStatusUpdate)
        socket.off(SocketEvent.WAITING_ROOM_DOCTORS_STATUS, handleWaitingRoomDoctorsStatus)
        appDispatch(leaveWaitingRoomSocketEvent(waitingRoomId))
        appDispatch(clearDoctorStatuses())
      }
    }

    connectSocket()
  }, [appDispatch, waitingRoomId])

  // Heartbeat mechanism for doctors
  useEffect(() => {
    if (!waitingRoomId || !authUser || !waitingRoomJoined) return

    // Set up heartbeat interval for doctors
    const heartbeatInterval = setInterval(() => {
      appDispatch(doctorHeartbeatSocketEvent({ waitingRoomId }))
    }, 20000) // Every 20 seconds

    return () => {
      clearInterval(heartbeatInterval)
    }
  }, [appDispatch, waitingRoomId, authUser, waitingRoomJoined])

  const handleAcceptConsultation = async (consultationId: string) => {
    if (!authUser || !waitingRoomJoined || !waitingRoomId) return

    setAcceptingConsultationId(consultationId)
    try {
      const { consultation } = await acceptConsultation({ consultationId, doctorId: authUser._id, waitingRoomId }).unwrap()
      // Check if this is a group consultation
      const waitingData = waitingConsultations.find((wc) => wc.id === consultationId)
      const livePath = waitingData?.isGroup
        ? `/consultations/${consultation._id}/group-live`
        : `/consultations/${consultation._id}/live`
      window.open(livePath, '_blank')
    } catch (error) {
      // The backend returns a typed `code` on 4xx (getApiErrorMessage only surfaces messages in
      // non-production, so we map the code to a localized snackbar ourselves).
      const code = (error as { data?: { code?: string; retryInSeconds?: number } })?.data?.code
      const seconds = (error as { data?: { retryInSeconds?: number } })?.data?.retryInSeconds ?? 0
      const codeMessages: Record<string, string> = {
        'waitingRoom.alreadyAccepted': t('notification.waitingRoom.acceptConsultation.alreadyAccepted'),
        'waitingRoom.capReached': t('notification.waitingRoom.acceptConsultation.capReached'),
        'waitingRoom.cooldown': t('notification.waitingRoom.acceptConsultation.cooldown', { seconds }),
        'waitingRoom.notMember': t('notification.waitingRoom.acceptConsultation.notMember'),
        'waitingRoom.notYourTurn': t('notification.waitingRoom.acceptConsultation.notYourTurn'),
      }
      const message = (code && codeMessages[code]) || getApiErrorMessage(error, t('notification.waitingRoom.acceptConsultation.error'))
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setAcceptingConsultationId(null)
    }
  }

  const handlePassOffer = async (consultationId: string) => {
    if (!waitingRoomId) return
    try {
      await passOffer({ waitingRoomId, consultationId }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.waitingRoom.acceptConsultation.error')), { variant: 'error' })
    }
  }

  if (isFetchingWaitingRoom) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center">
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!waitingRoom) return <NotFoundPage />

  return (
    <PageContainer sx={{ flex: 1 }}>
      <Paper sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Info name={waitingRoom.name} description={waitingRoom.description} />
          <Stack direction="row" alignItems="center" spacing={1} pr={2}>
            {waitingRoomJoined && <SettingButtons waitingRoomId={waitingRoom._id} />}
            <ActionsButton waitingRoomId={waitingRoom._id} />
          </Stack>
        </Stack>
        <Divider />
        {/* Opt-in (fairDistribution.prominentOffer) pulsing banner so the offered doctor cannot
            miss their turn — the row chip alone is easy to overlook. */}
        {fairStatus?.prominentOffer && offer?.isMyTurn && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              m: 2,
              p: 2,
              borderRadius: 2,
              color: '#fff',
              bgcolor: 'success.main',
              '@keyframes offerPulse': {
                '0%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0.55)' },
                '70%': { boxShadow: '0 0 0 14px rgba(46, 125, 50, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(46, 125, 50, 0)' },
              },
              animation: 'offerPulse 1.6s ease-out infinite',
            }}
            data-testid="prominent-offer-banner"
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <NotificationsActiveRoundedIcon fontSize="large" />
              <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
                {t('page.waitingRoom.offer.banner', { seconds: offerSecondsRemaining })}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LoadingButton
                loading={isAccepting}
                onClick={() => handleAcceptConsultation(offer.consultationId)}
                variant="contained"
                color="inherit"
                sx={{ color: 'success.dark', bgcolor: '#fff', fontWeight: 700, '&:hover': { bgcolor: '#f0f0f0' } }}
              >
                {t('common:button.accept.label')}
              </LoadingButton>
              <Button onClick={() => handlePassOffer(offer.consultationId)} disabled={isPassing} variant="outlined" color="inherit">
                {t('page.waitingRoom.offer.pass')}
              </Button>
            </Stack>
          </Stack>
        )}
        {(waitingRoomJoined || isCreator) && (
          <Tabs
            renderTabPanel={({ value }) => (
              <>
                <TabPanel value={value} index={0}>
                  <Box sx={{ p: '20px 16px' }}>
                    <WaitingConsultationsTable
                      data={waitingConsultations}
                      isAccepting={isAccepting}
                      acceptingConsultationId={acceptingConsultationId}
                      onAcceptConsultation={handleAcceptConsultation}
                      readinessEnabled={readinessEnabled}
                      acceptDisabledReason={acceptDisabledReason}
                      cooldownSecondsRemaining={cooldownSecondsRemaining}
                      offerMode={offerStrategy}
                      offer={offer}
                      offerSecondsRemaining={offerSecondsRemaining}
                      nextRoundSecondsRemaining={nextRoundSecondsRemaining}
                      isPassing={isPassing}
                      onPassOffer={handlePassOffer}
                      onPreviewConsultation={handlePreviewConsultation}
                    />
                  </Box>
                </TabPanel>
                <TabPanel value={value} index={1}>
                  <DoctorsTab
                    activeConsultations={activeConsultations}
                    waitingRoom={waitingRoom}
                    waitingRoomId={waitingRoom._id}
                    onPreviewConsultation={handlePreviewConsultation}
                  />
                </TabPanel>
              </>
            )}
            tabs={[
              { label: t('page.waitingRoom.tab.patients.label') },
              {
                label: activeConsultations.length
                  ? t('page.waitingRoom.tab.doctors.withInProgress.label', { count: activeConsultations.length })
                  : t('page.waitingRoom.tab.doctors.label'),
              },
            ]}
            variant="fullWidth"
          />
        )}
        {isSA && (
          <ConsultationPreviewDrawer
            waitingRoomId={waitingRoom._id}
            consultationId={previewConsultationId}
            onClose={() => setPreviewConsultationId(null)}
          />
        )}
      </Paper>
    </PageContainer>
  )
}
